import { Component, Input, NgZone, OnChanges } from '@angular/core';
import {
  SuburbDataService,
  SuburbNote,
  SuburbNoteInput,
} from '../../../../services/suburb-data.service';
import { apiErrorText } from '../workspace/api-errors';

/**
 * "Our data" on a suburb (#256): notes and facts Content Managers write, beside the crawl. Internal items are for the
 * team; public ones are written for the website (suburb page content will use them, plan §7). Plain text in our own
 * words, with where a fact came from. Shown as text only; source links are limited to http(s).
 */
@Component({
  selector: 'app-suburb-notes',
  templateUrl: './suburb-notes.component.html',
  styleUrls: ['./suburb-notes.component.scss'],
})
export class SuburbNotesComponent implements OnChanges {
  @Input() salCode!: string;
  @Input() readOnly = false;

  notes: SuburbNote[] = [];
  loading = false;
  error = '';
  /** The note being written: a new one (id null) or an edit. */
  draft: (SuburbNoteInput & { id: number | null }) | null = null;
  saving = false;
  readonly visibilities = [
    { label: 'Internal', value: 'internal' },
    { label: 'Public', value: 'public' },
  ];

  constructor(
    private data: SuburbDataService,
    private zone: NgZone
  ) {}

  ngOnChanges(): void {
    this.draft = null;
    this.load();
  }

  private fail(e: unknown): string {
    return apiErrorText(e, 'something went wrong');
  }

  load(): void {
    if (!this.salCode) return;
    this.loading = true;
    this.error = '';
    this.data.notes(this.salCode).subscribe({
      next: (n) => this.zone.run(() => ((this.notes = n), (this.loading = false))),
      error: (e) => this.zone.run(() => ((this.error = this.fail(e)), (this.loading = false))),
    });
  }

  add(): void {
    this.draft = {
      id: null,
      visibility: 'internal',
      title: '',
      body: '',
      source: '',
      sourceUrl: '',
    };
  }

  edit(n: SuburbNote): void {
    this.draft = {
      id: n.id,
      visibility: n.visibility,
      title: n.title ?? '',
      body: n.body,
      source: n.source ?? '',
      sourceUrl: n.sourceUrl ?? '',
    };
  }

  cancel(): void {
    this.draft = null;
  }

  save(): void {
    const d = this.draft;
    if (!d || !(d.body || '').trim()) return;
    const body: SuburbNoteInput = {
      visibility: d.visibility,
      title: d.title || null,
      body: d.body,
      source: d.source || null,
      sourceUrl: d.sourceUrl || null,
    };
    this.saving = true;
    const req =
      d.id === null
        ? this.data.addNote(this.salCode, body)
        : this.data.updateNote(this.salCode, d.id, body);
    req.subscribe({
      next: () =>
        this.zone.run(() => {
          this.saving = false;
          this.draft = null;
          this.load();
        }),
      error: (e) => this.zone.run(() => ((this.saving = false), (this.error = this.fail(e)))),
    });
  }

  remove(n: SuburbNote): void {
    this.data.deleteNote(this.salCode, n.id).subscribe({
      next: () => this.zone.run(() => this.load()),
      error: (e) => this.zone.run(() => (this.error = this.fail(e))),
    });
  }

  safeUrl(u: string | null): string | null {
    return u && /^https?:\/\//i.test(u) ? u : null;
  }
}
